import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TFunction } from "i18next/typescript/t";
import * as yup from "yup";

import { FilterField } from "@/components_v2/Filters/types";
import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import {
  HookFormInput,
  HookFormBaseDropdown,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { useEvidenceTemplates } from "@/domains/evidence/hooks/useEvidenceTemplates";
import {
  DataBlockRequestFiltersProps,
  DataBlockRequestFiltersState,
} from "@/domains/evidence-requests/types";

import "../styles.css";

const dataBlockRequestsFiltersSchema = (t: TFunction) =>
  yup
    .object()
    .shape({
      dataTemplates: yup
        .array()
        .of(
          yup
            .number()
            .required()
            .typeError(t("datablocks.errors.datablockTypeNotValid")),
        )
        .default([]),
      search: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .matches(/^[^<>*]+$/, t("datablocks.errors.specialCharsNotAllowed"))
        .max(100, t("datablocks.errors.searchTooLong")),
    })
    .required();

const RequestFilters = ({
  filterNames,
  setFilters,
  defaultFilters,
  className,
}: DataBlockRequestFiltersProps) => {
  const { t } = useTranslation();
  const dataTemplates = useEvidenceTemplates();

  const form = useForm<DataBlockRequestFiltersState>({
    // @ts-expect-error filters state is same as schema
    resolver: yupResolver(dataBlockRequestsFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      search: "",
      dataTemplates: [],
      ...(defaultFilters || {}),
    },
  });

  const dataTemplateTypeOptions = useMemo(() => {
    if (dataTemplates == null) return [];

    return dataTemplates.map((template) => ({
      label: template.title,
      value: template.id,
    }));
  }, [dataTemplates]);

  const filters: Record<string, FilterField> = useMemo(() => {
    return {
      type: {
        name: "dataTemplates",
        isAdvancedFilter: false,
        component: (
          <HookFormBaseDropdown
            name="dataTemplates"
            control={form.control}
            options={dataTemplateTypeOptions}
            multiple
            placeholder={t("datablocks.placeholders.dataTemplateType")}
            allowClear={true}
          />
        ),
      },
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="search"
            control={form.control}
            className="data-blocks-filters-search"
            suffix={<SearchOutlined />}
            placeholder={t("datablocks.placeholders.searchInvites")}
          />
        ),
      },
    };
  }, [dataTemplateTypeOptions]);

  return (
    <FilterRenderer
      className={className}
      filters={filters}
      activeFilterNames={filterNames}
      form={form}
      onFiltersChange={setFilters}
      popoverTitle={t("datablocks.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        setFilters(form.getValues());
      }}
      areFiltersDirty={form.formState.isDirty}
      handleSearch={() => setFilters(form.getValues())}
    />
  );
};

export default RequestFilters;
